import React, { FC, Suspense, useEffect, useRef, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Drawer } from 'src/components/widgets/drawer';
import ContentLoader from 'react-content-loader';
import theme from 'src/shared/theme';
import { ZSwitch } from 'src/components/widgets/zswitch';
import { T, backofficeUrl, toLocalID } from 'src/shared/helpers';
import type { RuleField } from '../common/types';
import { TOOL_EDITOR_CONFIGS, TOOL_DEFINITIONS } from '../common/constants';
import type { genericRuleEditor_MoqRuleProductCountQuery } from './__generated__/genericRuleEditor_MoqRuleProductCountQuery.graphql';
import type { genericRuleEditor_AIStagingRuleCountQuery } from './__generated__/genericRuleEditor_AIStagingRuleCountQuery.graphql';
import type { genericRuleEditor_AIVirtualModelRuleCountQuery } from './__generated__/genericRuleEditor_AIVirtualModelRuleCountQuery.graphql';

import { MoqRuleEditor } from './moqRuleEditor';
import { ProfanityRuleEditor } from './profanityRuleEditor';
import { AreaBackgroundRuleEditor } from './areaBackgroundRuleEditor';
import { AIImageGenEditor } from './aiImageGenEditor';
import { AIDesignGenEditor } from './aiDesignGenEditor';

import { BulkVariationEditor } from './bulkVariationEditor';

export interface GenericRuleEditorProps {
    toolKey: string;
    itemId: string;
    productId: string;
    productModelId?: number;
    onBack: () => void;
    onGoToFullTool: () => void;
}
import {
    StackedBreadcrumb,
    StackedBody,
    StackedFooterButtons,
    FooterButton,
    FullModeLink,
    FullModeHint,
    FullModeIframeContainer,
    FullModeSkeleton,
    SectionCard,
    SectionCardTitle,
    FormGroup,
    FormLabel,
    FormInputCompact,
    FormSelect,
    FormSwitchRow,
    FormSwitchLabel,
    SharedWarningCallout,
    IframeEmbed
} from '../common/styled';

const MoqProductCountCallout: FC<{ itemId: string }> = ({ itemId }) => {
    const data = useLazyLoadQuery<genericRuleEditor_MoqRuleProductCountQuery>(
        graphql`
            query genericRuleEditor_MoqRuleProductCountQuery($id: ID!) {
                moqRule(id: $id) {
                    products {
                        productID
                    }
                }
            }
        `,
        { id: itemId }
    );
    const count = data?.moqRule?.products?.length;
    if (count == null || count <= 1) return null;
    return (
        <SharedWarningCallout>
            {T._('This rule is assigned to', 'Admin')} <strong>{count}</strong> {T._('products. Any changes will apply to all of them.', 'Admin')}
        </SharedWarningCallout>
    );
};

const AIStagingProductCountCallout: FC<{ itemId: string }> = ({ itemId }) => {
    const data = useLazyLoadQuery<genericRuleEditor_AIStagingRuleCountQuery>(
        graphql`
            query genericRuleEditor_AIStagingRuleCountQuery($id: ID!) {
                aiProductStagingRule(id: $id) {
                    productIDs
                }
            }
        `,
        { id: itemId }
    );
    const count = data?.aiProductStagingRule?.productIDs?.length;
    if (count == null || count <= 1) return null;
    return (
        <SharedWarningCallout>
            {T._('This rule is assigned to', 'Admin')} <strong>{count}</strong> {T._('products. Any changes will apply to all of them.', 'Admin')}
        </SharedWarningCallout>
    );
};

const AIVirtualModelProductCountCallout: FC<{ itemId: string }> = ({ itemId }) => {
    const data = useLazyLoadQuery<genericRuleEditor_AIVirtualModelRuleCountQuery>(
        graphql`
            query genericRuleEditor_AIVirtualModelRuleCountQuery($id: ID!) {
                aiVirtualModelRule(id: $id) {
                    productIDs
                }
            }
        `,
        { id: itemId }
    );
    const count = data?.aiVirtualModelRule?.productIDs?.length;
    if (count == null || count <= 1) return null;
    return (
        <SharedWarningCallout>
            {T._('This rule is assigned to', 'Admin')} <strong>{count}</strong> {T._('products. Any changes will apply to all of them.', 'Admin')}
        </SharedWarningCallout>
    );
};

const ToolProductCountCallout: FC<{ toolKey: string; itemId: string }> = ({ toolKey, itemId }) => {
    if (toolKey === 'moq') return <MoqProductCountCallout itemId={itemId} />;
    if (toolKey === 'ai-lifestyle-scenes') return <AIStagingProductCountCallout itemId={itemId} />;
    if (toolKey === 'ai-try-on') return <AIVirtualModelProductCountCallout itemId={itemId} />;
    return null;
};

const GenericRuleEditorContent: FC<GenericRuleEditorProps> = ({ toolKey, itemId, productId, productModelId, onBack, onGoToFullTool }) => {
    const toolName = TOOL_DEFINITIONS.find((tool) => tool.key === toolKey)?.name || '';
    const productNameData = useLazyLoadQuery<any>(
        graphql`
            query genericRuleEditor_ProductNameQuery($id: ID!) {
                node(id: $id) {
                    ... on Model {
                        name
                    }
                }
            }
        `,
        { id: productId }
    );
    const productName = (productNameData?.node as any)?.name || '';
    const editorConfig = TOOL_EDITOR_CONFIGS[toolKey];
    const toolDef = TOOL_DEFINITIONS.find((t) => t.key === toolKey);
    const isCreateNew = !itemId;
    const hasBuildableEmbedded = !!toolDef?.buildEmbeddedUrl || !!toolDef?.buildSetupRoute;
    const [fullMode, setFullMode] = useState(
        !!toolDef?.fullModeOnlyInSidebar ||
            (isCreateNew && hasBuildableEmbedded && !toolDef?.quickModeOnly) ||
            !!toolDef?.fullModeOnly ||
            (!TOOL_EDITOR_CONFIGS[toolKey] && !toolDef?.dedicatedEditor)
    );

    useEffect(() => {
        // bulk-variations auto-saves on every state change (including the initial attributes
        // load), so reacting to 'embedded-tool-saved' would close the editor immediately after
        // opening. The user closes it manually in that case.
        if (toolKey === 'bulk-variations') return;
        const handleMessage = (e: MessageEvent) => {
            if (e.data?.type === 'embedded-tool-saved') {
                onBack();
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [onBack, toolKey]);
    const [iframeLoading, setIframeLoading] = useState(true);
    const [openGalleryOnFullMode, setOpenGalleryOnFullMode] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const editorSaveRef = useRef<(() => Promise<void>) | null>(null);
    const [editorCanSave, setEditorCanSave] = useState(true);
    const [values, setValues] = useState<Record<string, boolean | string | number>>(() => {
        const defaults: Record<string, boolean | string | number> = {};
        if (editorConfig) {
            editorConfig.fields.forEach((field) => {
                defaults[field.key] = field.defaultValue;
            });
        }
        return defaults;
    });

    const setField = (key: string, value: boolean | string | number) => {
        setValues((prev) => ({ ...prev, [key]: value }));
    };

    const sections = editorConfig ? ([...new Set(editorConfig.fields.map((field) => field.section).filter(Boolean))] as string[]) : [];
    const localProductId = productModelId != null ? String(productModelId) : String(toLocalID(productId) ?? '');
    const embeddedUrl = (() => {
        const base = isCreateNew
            ? toolDef?.buildSetupRoute
                ? toolDef.buildSetupRoute(localProductId) + '?embedded=true&fromProductId=' + localProductId
                : toolDef?.buildEmbeddedUrl?.(itemId, localProductId)
            : toolDef?.buildEmbeddedUrl?.(itemId, productId);
        if (base && toolKey === 'bulk-variations' && isCreateNew) return base + '&fromEnable=true';
        return base;
    })();
    const hasDedicatedEditor = !!toolDef?.dedicatedEditor;

    // Tools with no editor config and no embedded URL can't be edited
    if (!isCreateNew && !editorConfig && !embeddedUrl && !hasDedicatedEditor) return null;

    return (
        <Drawer
            isOpen={true}
            isStacked
            onClose={onBack}
            header={fullMode ? toolName : { title: toolName, subtitle: editorConfig?.subtitle || productName }}
            headerActions={
                fullMode && toolDef?.helpUrl ? (
                    <a
                        href={toolDef.helpUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        style={{ fontSize: '13px', fontWeight: 400, color: '#3b82f6', textDecoration: 'none', whiteSpace: 'nowrap' }}
                    >
                        {T._('Guide', 'Admin')}
                    </a>
                ) : undefined
            }
            width={fullMode ? window.innerWidth - 80 : 620}
            footer={
                <>
                    {!fullMode && !toolDef?.fullModeOnly && toolKey !== 'profanity' && (
                        <FullModeHint>{T._('+ more settings available in full mode', 'Admin')}</FullModeHint>
                    )}
                    {embeddedUrl && !fullMode ? (
                        <FullModeLink
                            onClick={() => {
                                setFullMode(true);
                                setIframeLoading(true);
                            }}
                        >
                            {'↗ ' + T._('Full mode', 'Admin')}
                        </FullModeLink>
                    ) : embeddedUrl &&
                      fullMode &&
                      !isCreateNew &&
                      !toolDef?.fullModeOnly &&
                      !toolDef?.fullModeOnlyInSidebar &&
                      (editorConfig || hasDedicatedEditor) ? (
                        <FullModeLink onClick={() => setFullMode(false)}>{'↙ ' + T._('Quick mode', 'Admin')}</FullModeLink>
                    ) : !embeddedUrl ? (
                        <FullModeLink onClick={onGoToFullTool}>&#8599; {T._('Full mode', 'Admin')}</FullModeLink>
                    ) : null}
                    {!fullMode && (
                        <StackedFooterButtons>
                            <FooterButton onClick={onBack}>{T._('Cancel', 'Admin')}</FooterButton>
                            <FooterButton
                                $primary
                                onClick={async () => {
                                    if (hasDedicatedEditor && editorSaveRef.current) {
                                        setIsSaving(true);
                                        await editorSaveRef.current();
                                        setIsSaving(false);
                                    } else {
                                        onBack();
                                    }
                                }}
                                disabled={isSaving || !editorCanSave}
                            >
                                {isSaving ? T._('Saving...', 'Admin') : T._('Save Changes', 'Admin')}
                            </FooterButton>
                        </StackedFooterButtons>
                    )}
                </>
            }
        >
            {fullMode && embeddedUrl ? (
                <FullModeIframeContainer>
                    {iframeLoading && (
                        <FullModeSkeleton>
                            <ContentLoader
                                speed={2}
                                width='100%'
                                height='516'
                                backgroundColor={theme.skeleton.backgroundColor}
                                foregroundColor={theme.skeleton.foregroundColor}
                            >
                                <rect x='0' y='0' rx='8' ry='8' width='30%' height='28' />
                                <rect x='0' y='52' rx='8' ry='8' width='100%' height='16' />
                                <rect x='0' y='76' rx='8' ry='8' width='80%' height='16' />
                                <rect x='0' y='116' rx='8' ry='8' width='60%' height='40' />
                                <rect x='0' y='180' rx='8' ry='8' width='100%' height='16' />
                                <rect x='0' y='204' rx='8' ry='8' width='90%' height='16' />
                                <rect x='0' y='244' rx='8' ry='8' width='45%' height='40' />
                                <rect x='0' y='316' rx='8' ry='8' width='100%' height='200' />
                            </ContentLoader>
                        </FullModeSkeleton>
                    )}
                    <IframeEmbed
                        src={backofficeUrl(embeddedUrl + (openGalleryOnFullMode ? '&openGallery=true' : ''))}
                        $loading={iframeLoading}
                        title={toolName}
                        onLoad={() => setIframeLoading(false)}
                    />
                </FullModeIframeContainer>
            ) : toolKey === 'profanity' ? (
                <ProfanityRuleEditor
                    itemId={itemId}
                    productId={productId}
                    toolKey={toolKey}
                    onBack={onBack}
                    onSave={onBack}
                    saveRef={editorSaveRef}
                    onCanSaveChange={setEditorCanSave}
                />
            ) : toolKey === 'moq' ? (
                <MoqRuleEditor
                    itemId={itemId}
                    productId={productId}
                    toolKey={toolKey}
                    onBack={onBack}
                    onSave={onBack}
                    saveRef={editorSaveRef}
                    onCanSaveChange={setEditorCanSave}
                />
            ) : toolKey === 'area-background' ? (
                <AreaBackgroundRuleEditor
                    itemId={itemId}
                    productId={productId}
                    toolKey={toolKey}
                    onBack={onBack}
                    onSave={onBack}
                    saveRef={editorSaveRef}
                    onCanSaveChange={setEditorCanSave}
                    onSwitchToFullMode={() => {
                        setOpenGalleryOnFullMode(true);
                        setFullMode(true);
                        setIframeLoading(true);
                    }}
                />
            ) : toolKey === 'ai-image-generation' ? (
                <AIImageGenEditor
                    itemId={itemId}
                    productId={productId}
                    toolKey={toolKey}
                    onBack={onBack}
                    onSave={onBack}
                    saveRef={editorSaveRef}
                    onCanSaveChange={setEditorCanSave}
                />
            ) : toolKey === 'ai-design-generation' ? (
                <AIDesignGenEditor
                    itemId={itemId}
                    productId={productId}
                    toolKey={toolKey}
                    onBack={onBack}
                    onSave={onBack}
                    saveRef={editorSaveRef}
                    onCanSaveChange={setEditorCanSave}
                />
            ) : toolKey === 'bulk-variations' ? (
                <BulkVariationEditor
                    itemId={itemId}
                    productId={productId}
                    toolKey={toolKey}
                    onBack={onBack}
                    onSave={onBack}
                    saveRef={editorSaveRef}
                    onCanSaveChange={setEditorCanSave}
                />
            ) : (
                <>
                    <StackedBreadcrumb>
                        <span onClick={onBack}>{productName}</span> / {toolName}
                    </StackedBreadcrumb>

                    <StackedBody>
                        <ToolProductCountCallout toolKey={toolKey} itemId={itemId} />
                        {(() => {
                            const filterDeps = (field: RuleField) => {
                                if (!field.dependsOn) return true;
                                if (field.dependsOn.includes(':')) {
                                    const [depKey, depVal] = field.dependsOn.split(':');
                                    return values[depKey] === depVal;
                                }
                                return !!values[field.dependsOn];
                            };
                            const renderField = (field: RuleField) => {
                                if (field.type === 'switch') {
                                    return (
                                        <FormSwitchRow key={field.key}>
                                            <FormSwitchLabel>{field.label}</FormSwitchLabel>
                                            <ZSwitch checked={!!values[field.key]} onChange={() => setField(field.key, !values[field.key])} />
                                        </FormSwitchRow>
                                    );
                                }
                                if (field.type === 'number' || field.type === 'input' || field.type === 'text') {
                                    return (
                                        <FormGroup key={field.key}>
                                            <FormLabel>{field.label}</FormLabel>
                                            <FormInputCompact
                                                type={field.type === 'number' ? 'number' : 'text'}
                                                value={values[field.key]?.toString() || ''}
                                                onChange={(e) => setField(field.key, e.target.value)}
                                                $width={field.type === 'number' ? 120 : undefined}
                                            />
                                        </FormGroup>
                                    );
                                }
                                if (field.type === 'select' && field.options) {
                                    return (
                                        <FormGroup key={field.key}>
                                            <FormLabel>{field.label}</FormLabel>
                                            <FormSelect value={values[field.key]?.toString() || ''} onChange={(e) => setField(field.key, e.target.value)}>
                                                {field.options.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </FormSelect>
                                        </FormGroup>
                                    );
                                }
                                return null;
                            };
                            const sectionlessFields = editorConfig.fields.filter((field) => !field.section).filter(filterDeps);
                            return (
                                <>
                                    {sectionlessFields.length > 0 && <SectionCard>{sectionlessFields.map(renderField)}</SectionCard>}
                                    {sections.map((section) => (
                                        <SectionCard key={section}>
                                            <SectionCardTitle>{section}</SectionCardTitle>
                                            {editorConfig.fields
                                                .filter((field) => field.section === section)
                                                .filter(filterDeps)
                                                .map(renderField)}
                                        </SectionCard>
                                    ))}
                                </>
                            );
                        })()}
                    </StackedBody>
                </>
            )}
        </Drawer>
    );
};

export const GenericRuleEditor: FC<GenericRuleEditorProps> = (props) => {
    return (
        <Suspense fallback={null}>
            <GenericRuleEditorContent {...props} />
        </Suspense>
    );
};
